import {
  Component,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
  HostListener,
} from '@angular/core';
import { MatMenuTrigger } from '@angular/material/menu';
import { ActivatedRoute, NavigationEnd, Params, Router } from '@angular/router';

import { AuthService } from '@services/http/auth.service';
import { ExternalLinksService } from '@app/services/Logic/externalLinks.service';
import { HistoryChangesService } from '@services/Logic/historyChanges.service';
import { StateService } from '@services/Logic/state.service';
import { StorageService } from '@services/Logic/storage.service';
import { TranslateService } from '@services/Logic/translate.service';

import { Subscription } from 'rxjs';
import { VerticalLinkBoxComponent } from '@components/headerNotAuthenticated/verticalLinkBox/verticalLinkBox';

import { environment } from '../../../environments/environment';

@Component({
  selector: 'app-header-not-authenticated',
  templateUrl: './header-not-authenticated.component.html',
  styleUrls: ['./header-not-authenticated.component.scss'],
})
export class HeaderNotAuthenticatedComponent implements OnInit, OnDestroy {
  @Input() newMessagesCount: number;
  @ViewChild(MatMenuTrigger) trigger: MatMenuTrigger;
  @ViewChild(VerticalLinkBoxComponent)
  verticalLinkBoxComponent: VerticalLinkBoxComponent;
  initLanguageRef: Subscription;
  private currentLanguage: any;
  languages: { name: string; value: string; flag: string }[];
  index = 0;
  isMenuOpen = false;
  verticalsHasBeenFetched = false;
  defaultImage = `${environment.basePath}assets/svg/logo.svg`;
  logo = `${environment.cloudFrontImgUrl}/private/compressed/lp/webp/hh_logo_blue.webp`;
  currentTranslationLanguageTrigger: string;
  showLangImg;
  /**
   * URL param. Redirect URL after Auth.
   */
  redirectUrl: string;
  isMobileScreen = false;
  currentParams;
  innerWidth = window.innerWidth;

  showHeader: boolean = true;

  constructor(
    private authService: AuthService,
    private route: ActivatedRoute,
    private historyChangesService: HistoryChangesService,
    private router: Router,
    private stateService: StateService,
    private translateService: TranslateService,
    public storage: StorageService,
    public externalLinksService: ExternalLinksService,
  ) {
    this.languages = [
      { name: 'Danish', value: 'dk', flag: 'dk' },
      { name: 'English', value: 'en', flag: 'gb' },
    ];
  }

  loadFlagImg() {
    this.showLangImg = true;
  }

  get lang() {
    return this.storage.lang;
  }

  @HostListener('window:resize', ['$event'])
  onResize = () => {
    this.innerWidth = window.innerWidth;
    this.isMobileScreen = this.innerWidth <= 700;
  };

  ngOnInit() {
    this.handleAuthParams();
    this.innerWidth = window.innerWidth;
    this.isMobileScreen = this.innerWidth <= 700;
    this.initLanguage(this.storage.lang);
    this.initSubscriptions();
    this.detectHistoryChanges();

    this.stateService.showHeaderSource.subscribe((res) => {
      this.showHeader = res;
    });

    this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.stateService.showHeader(
          !event.urlAfterRedirects.includes('/opret'),
        );
      }
    });
  }

  initSubscriptions() {
    this.initLanguageRef = this.stateService.initLanguageSource.subscribe(
      (res) => {
        if (res.lang) {
          this.initLanguage(res.lang);
        }
      },
    );
  }

  ngOnDestroy() {
    this.initLanguageRef?.unsubscribe();
  }

  detectHistoryChanges() {
    this.historyChangesService.detectHistoryChanges();
  }

  private initLanguage(lang: any) {
    let setLang = lang;
    if (!lang) {
      setLang = this.languages[0];
    }
    this.updateLocalLanguage(setLang, false);
  }

  public updateLocalLanguage(lang: any, reloadPage: boolean) {
    this.currentLanguage = lang;
    this.storage.lang = lang;
    this.translateService.use(lang.value);
    if (reloadPage) {
      window.location.reload();
    }
  }

  // Toggles between a preset list of languages, assumes only two languages for toggling
  toggleLanguage() {
    const isEnglish = this.currentLanguage.value === 'en';
    const nextLang = isEnglish ? this.languages[1] : this.languages[0];
    this.updateLocalLanguage(nextLang, true);
  }

  openAuthPage() {
    this.currentParams = this.router.url;
    return this.router.navigate(['/tilmeld', { priorUrl: this.currentParams }]);
  }

  menuOpened() {
    this.loadFlagImg();
    this.isMenuOpen = true;
    this.verticalsHasBeenFetched = true;
  }

  menuClosed() {
    this.isMenuOpen = false;
  }

  goToUserHappyHelper(): void {
    window.open(
      'https://happyhelper.dk?utm_medium=website_menu&utm_source=handyhand&utm_campaign=happy_handy_colab',
    );
  }

  /**
   * If auth query params, open authentification dialog, and redirect user on close.
   * @returns
   */
  private handleAuthParams() {
    this.route.queryParams.subscribe((params: Params) => {
      this.redirectUrl = params.redirectUrl;
      if (params.authDialog != null) {
        if (this.authService.isAuthenticated()) {
          if (this.redirectUrl) {
            this.router.navigateByUrl(this.redirectUrl);
          }
          return;
        }
        this.openAuthPage();
      }
    });
  }

  protected readonly environment = environment;
}
